import React, { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { handleGetRequest } from '../../utils/api-handler.util'
import CountdownTimer from '../../components/CountdownTimer'
import { QueryKeys } from '../../utils/queryKeyGenerator'
import useProductCategories from '../../hooks/useProductCategories'
import BackButtonAndTitle from '../../components/retailer-shop/BackButtonAndTitle'
import {
  InformationCircleIcon,
  TrophyIcon,
  BoltIcon,
  ExclamationCircleIcon,
} from '@heroicons/react/24/outline'
import { formatTime } from '../../utils/date.util'
import RespondBidModal from '../../components/retailer/requests/RespondBidModal'
import RespondFinderModal from '../../components/retailer/requests/RespondFinderModal'
import ReportButton from '../../components/common/ReportButton'
import { CONTENT_TYPES } from '../../constants/contentTypes'
import { MESSAGE_LISTING_REFETCH_INTERVAL_MILLIS } from '../../constants/messagingConstants'
import DisplayBidStatus from '../../components/common/DisplayBidStatus'
import { useAuth } from '../../contexts/AuthContext'

const RetailerProductFinderResponsePage = () => {
  const { id } = useParams()
  const { getCategoryNameById } = useProductCategories()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { currentUser } = useAuth()
  const navigate = useNavigate()

  const { data: bidRequestData } = useQuery({
    queryKey: QueryKeys.retailerBidRequestById(id),
    queryFn: () => handleGetRequest(`/retailers/bid-requests/${id}`),
    enabled: !!id,
    refetchInterval: (queryData) => {
      const bid = queryData?.state?.data?.data || {}
      return bid?.isOpen ? MESSAGE_LISTING_REFETCH_INTERVAL_MILLIS : false
    },
  })

  const { retailerResponse, lowestBid } = bidRequestData?.data || {}
  const bidRequest = bidRequestData?.data

  const isExpired = () => {
    if (!bidRequest?.expiresAt) return false

    const expiryTimestamp = new Date(bidRequest?.expiresAt).getTime()
    return expiryTimestamp < Date.now()
  }

  const renderClosingTime = () => {
    if (!bidRequest?.isOpen) return null
    if (!bidRequest?.expiresAt) return null

    const expiryTimestamp = new Date(bidRequest?.expiresAt).getTime()

    if (isExpired()) {
      return
    }

    return (
      <div className="flex gap-4 items-center mt-4">
        <span className="text-[16px] text-black/70 font-normal">Closing in</span>
        <CountdownTimer expiryTimestamp={expiryTimestamp} />
      </div>
    )
  }

  const renderResponses = () => {
    if (!currentUser?.activeSubscription) {
      return (
        <div className="flex items-center gap-5 justify-between h-full mt-10 bg-red-50 border-red-200 border rounded-lg p-4">
          <ExclamationCircleIcon className="h-20 w-20 text-red-600" />
          <span className="text-md text-red-600 flex-1">
            You need an active subscription to respond to this request.
            <button
              className="text-red-600 underline font-bold"
              onClick={() => navigate('/subscription')}
            >
              Click here
            </button>{' '}
            to subscribe now.
          </span>
        </div>
      )
    }
    if ((isExpired() || !bidRequest?.isOpen) && !retailerResponse?.id) {
      return <></>
    }

    if (!retailerResponse?.id) {
      return (
        <div className="flex items-center gap-5 justify-between h-full mt-10 bg-secondary-600/5 border-secondary-600/20 border rounded-lg p-4 ">
          <InformationCircleIcon className="h-20 w-20 text-secondary-600" />
          <span className="text-md text-secondary-600 flex-1">
            You have not responded to this request yet.
            <button className="text-secondary-600 underline font-bold" onClick={handleRespond}>
              Click here
            </button>{' '}
            to respond now.
          </span>
        </div>
      )
    }

    const isMyBidLowest = retailerResponse?.bidPrice === lowestBid?.bidPrice

    return (
      <>
        <h6 className="mb-2 font-bold text-black">My Response</h6>
        <div className="mt-2 mb-4 border border-secondary-100 rounded-[5px] bg-background p-4 flex gap-4">
          {retailerResponse?.productImage && (
            <img
              src={retailerResponse?.productImage}
              alt={retailerResponse?.productName}
              className=" aspect-square object-contain h-[100px]"
            />
          )}
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <div className="font-semibold text-lg text-black">
                {retailerResponse?.productName}
              </div>
              {isMyBidLowest && (
                <span className="bg-blue-100 text-blue-800 text-xs font-bold px-2 py-1 rounded border border-blue-300 flex items-center gap-1">
                  <TrophyIcon className="h-3 w-3" />
                  WINNING BID
                </span>
              )}
            </div>

            <div className="mt-3 bg-neutral-200-50 rounded-lg p-3 border">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">Your Original Bid</span>
                <span className="font-bold text-md text-gray-900">
                  {retailerResponse?.finalPriceText}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">Customer Sees</span>
                <div className="flex items-center gap-2">
                  <span className="font-bold text-md text-blue-600">
                    {retailerResponse?.bidPriceText}
                  </span>
                </div>
              </div>
              <div>
                {isMyBidLowest ? (
                  <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                    LOWEST
                  </span>
                ) : (
                  <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded-full">
                    COMPETING
                  </span>
                )}
              </div>
            </div>

            <div className="text-sm text-gray-500 mt-3">
              Responded at: {formatTime(retailerResponse?.createdAt)}
            </div>
          </div>
        </div>
      </>
    )
  }

  const renderLowestBid = () => {
    if (!lowestBid?.id) return null

    const isMyBidLowest = retailerResponse?.bidPrice === lowestBid?.bidPrice
    if (isMyBidLowest && retailerResponse?.id) return null

    return (
      <>
        <div className="flex items-center gap-2 mb-2">
          <h6 className="font-bold text-black">Competition Alert</h6>
          <span className="bg-red-100 text-red-800 text-xs font-bold px-2 py-1 rounded border border-red-300 flex items-center gap-1">
            <BoltIcon className="h-3 w-3" />
            BETTER BID EXISTS
          </span>
        </div>
        <div className="mt-2 mb-4 border-2 border-red-200 rounded-[5px] bg-red-50 p-4 flex gap-4">
          {lowestBid?.productImage && (
            <img
              src={lowestBid?.productImage}
              alt={lowestBid?.productName}
              className=" aspect-square object-contain h-[100px]"
            />
          )}
          <div className="flex-1">
            <div className="font-semibold text-lg text-black">{lowestBid?.productName}</div>
            <div className="flex items-center gap-2 mt-2">
              <span className="text-sm font-medium text-gray-700">Current Best Price:</span>
              <span className="font-bold text-xl text-red-600">{lowestBid?.bidPriceText}</span>
            </div>
            <div className="text-sm text-gray-500 mt-2">
              Submitted: {formatTime(lowestBid?.createdAt)}
            </div>
          </div>
        </div>
      </>
    )
  }
  const handleCloseModal = (refresh) => {
    setIsModalOpen(false)

    if (refresh) {
      QueryKeys.retailerBidRequestById.invalidate(id)
    }
  }

  const handleRespond = async () => {
    setIsModalOpen(true)
  }

  const isBid = !!bidRequest?.productId

  return (
    <div className="main-container">
      <div className="flex items-center justify-between ">
        <BackButtonAndTitle
          navigateTo={'/retailer/products/requests'}
          title={isBid ? 'Bid Request Details' : `Product Finder Details`}
        />
        {!isBid && (
          <ReportButton
            contentId={bidRequest?.productFinderId}
            contentType={CONTENT_TYPES.productFinder}
            reportTarget={'Product Finder Request'}
          />
        )}
      </div>

      <div className="mt-2 mb-4 border border-secondary-100 rounded-[5px] bg-background p-4">
        <div className="flex flex-col">
          <img
            src={bidRequest?.imageUrl}
            alt={bidRequest?.description}
            className="w-full aspect-square object-contain h-[200px] mb-4"
          />
          <div className="flex-1">
            <div className="flex justify-between items-center mb-2">
              <span className="font-semibold text-lg text-black">
                {bidRequest?.productName ||
                  getCategoryNameById(bidRequest?.categoryId) ||
                  'No Name'}
              </span>
              <div className="flex gap-2">
                <DisplayBidStatus isOpen={bidRequest?.isOpen} expiresAt={bidRequest?.expiresAt} />

                {bidRequest?.productFinderId && (
                  <span className="italic text-[#E7790C] font-medium text-sm">
                    FND-{bidRequest?.productFinderId}
                  </span>
                )}
              </div>
            </div>

            <div className="mb-3">
              {!bidRequest?.isOpen && bidRequest?.closingReason && (
                <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-md">
                  <div className="text-sm text-red-600">
                    <span className="font-medium">Closing Reason:</span> {bidRequest.closingReason}
                  </div>
                </div>
              )}
            </div>

            {bidRequest?.productPrice && (
              <div className="text-sm text-black font-normal mt-1">
                Start Bidding At: {bidRequest?.productPriceText}
              </div>
            )}
            <div className="text-sm text-black font-normal mt-1">
              Quantity: {bidRequest?.requestQuantity}
            </div>
            <div className="text-sm text-gray-500 mt-1 line-clamp-2">
              {bidRequest?.productFinderDescription}
            </div>
          </div>
        </div>
        {renderClosingTime()}
      </div>
      {renderLowestBid()}
      {renderResponses()}
      {bidRequest?.id && (
        <>
          <RespondBidModal
            isOpen={isModalOpen && isBid}
            onClose={handleCloseModal}
            requestId={bidRequest.id}
            productId={bidRequest.productId}
            productPrice={bidRequest.productPrice}
            productPriceText={bidRequest.productPriceText}
          />
          <RespondFinderModal
            isOpen={isModalOpen && !isBid}
            onClose={handleCloseModal}
            request={bidRequest}
          />
        </>
      )}
    </div>
  )
}

export default RetailerProductFinderResponsePage
